import React from 'react';
import { Box, Container, Typography, Toolbar, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FooterSection from '../components/FooterSection';

const productCards = {
  darwin: {
    title: 'Darwin - Product Overview',
    description:
      'Explore the full Darwin specification, supported industries, and everything your pipe-mounted sensor can do.',
    path: '/darwin',
  },
  eden: {
    title: 'Eden - Product Overview',
    description:
      'Explore the full Eden specification and everything your environmental temperature & humidity sensor can do.',
    path: '/eden',
  },
};

const heroTitles = {
  darwin: 'Get started with Darwin',
  eden: 'Get started with Eden',
  both: 'Get started with Darwin & Eden',
};

const CustomerLandingPage = () => {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const company =
    params.get('company') || sessionStorage.getItem('customerCompany') || '';
  const rawProduct =
    params.get('product') || sessionStorage.getItem('customerProduct') || '';

  const products = rawProduct
    .toLowerCase()
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p === 'darwin' || p === 'eden');

  const hasDarwin = products.includes('darwin') || products.length === 0;
  const hasEden = products.includes('eden');
  const heroTitle =
    hasDarwin && hasEden
      ? heroTitles.both
      : hasEden
      ? heroTitles.eden
      : heroTitles.darwin;

  const cards = [
    {
      number: '01',
      title: 'Installation Guide',
      description:
        'Step-by-step instructions for mounting and activating your sensor, including a full video walkthrough.',
      path: '/installation',
    },
    ...(hasDarwin ? [{ number: hasDarwin && hasEden ? '02' : '02', ...productCards.darwin }] : []),
    ...(hasEden
      ? [{ number: hasDarwin ? '03' : '02', ...productCards.eden }]
      : []),
    {
      number: hasDarwin && hasEden ? '04' : '03',
      title: 'FAQs',
      description:
        'Answers to common questions on connectivity, battery life, compliance reporting, and troubleshooting.',
      path: '/faqs',
    },
  ];

  return (
    <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Toolbar sx={{ height: '68px' }} />

      {/* Hero */}
      <Box
        sx={{
          backgroundColor: '#000000',
          py: { xs: 10, md: 14 },
          borderBottom: '1px solid #1a1a1a',
        }}
      >
        <Container maxWidth="lg">
          {company && (
            <Typography
              sx={{
                fontFamily: 'Outfit',
                fontWeight: 700,
                fontSize: '0.8rem',
                color: '#004D40',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              Welcome, {company}
            </Typography>
          )}
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.75rem' },
              color: '#ffffff',
              letterSpacing: '-0.02em',
              mb: 3,
              maxWidth: 700,
              lineHeight: 1.1,
            }}
          >
            {heroTitle}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 300,
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.6)',
              maxWidth: 520,
              mb: 5,
            }}
          >
            Everything you need to install, configure, and monitor your sensors - all in one place.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/installation')}
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 600,
              fontSize: '0.95rem',
              backgroundColor: '#004D40',
              color: '#ffffff',
              borderRadius: 1,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#00695C' },
            }}
          >
            View Installation Guide
          </Button>
        </Container>
      </Box>

      {/* Cards */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {cards.map((card, i) => (
              <Box
                key={card.number}
                sx={{
                  display: 'flex',
                  gap: { xs: 3, md: 5 },
                  pb: i < cards.length - 1 ? 6 : 0,
                  position: 'relative',
                }}
              >
                {/* Number + connecting line */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: '#004D40',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Outfit',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        color: '#ffffff',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {card.number}
                    </Typography>
                  </Box>
                  {i < cards.length - 1 && (
                    <Box sx={{ width: '2px', flex: 1, backgroundColor: '#e8e8e8', mt: 1 }} />
                  )}
                </Box>

                {/* Content */}
                <Box sx={{ pt: 0.75 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      color: '#000000',
                      mb: 1,
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Outfit',
                      fontWeight: 400,
                      fontSize: '0.97rem',
                      color: '#555555',
                      lineHeight: 1.75,
                      mb: 1.5,
                    }}
                  >
                    {card.description}
                  </Typography>
                  <Typography
                    onClick={() => navigate(card.path)}
                    sx={{
                      fontFamily: 'Outfit',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#004D40',
                      cursor: 'pointer',
                      display: 'inline-block',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Go to {card.title} →
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <FooterSection />
    </Box>
  );
};

export default CustomerLandingPage;
